import {
  Animator,
  IColumn,
  IGraph,
  INode,
  INodeStyle,
  Insets,
  InteriorNodeLabelModel,
  NodeStyleStripeStyleAdapter,
  ShapeNodeStyle,
  Table,
  TableNodeStyle
} from '@yfiles/yfiles';

import { graphDataSchema } from './types_zod.ts';
import { Capability, GraphData, Product, StreamStage } from './types.ts';
import { isSameLeafNodeIdentity, KeyUtils } from './keyUtils';
import { columnHeaderStyle, getProductNodeStyle, getProductEdgeStyle, getAnimatedProductEdgeStyle, productLabelStyle } from './styles.ts';
import { getRevenue } from './util.ts';
import { checkSchema } from './defaults.ts';
import {
  createAppGroupNode,
  createLeafNode,
  createCapaGroupNode,
  createTeamNode
} from './nodeCreators.ts';
import { createOrderedLeafPathForProduct } from './graph/flowPathBuilder/flowPathBuilder';

export default function loadGraph(
  graph: IGraph,
  jsonData: GraphData,
  animator?: Animator,
  dotPixelsPerSecond?: number
): { stages: string[]; products: Product[] } {
  let nodeId = 0;

  if (checkSchema) {
    try {
      graphDataSchema.parse(jsonData);
    } catch (e) {
      throw new Error(`Failed to parse input data: ${e}`);
    }
  }

  graph.clear();

  // Read product definitions
  const productsMap = new Map<string, Product>();
  const productsArray: Product[] = [];
  let maxRevenue = 0;
  jsonData.products.forEach((product) => {
    productsMap.set(product.Name, product);
    productsArray.push(product);
    maxRevenue = Math.max(maxRevenue, parseInt(product.Revenue));
  });

  productsArray.sort((a, b) => {
    const revenueA = getRevenue(a);
    const revenueB = getRevenue(b);
    return revenueA > revenueB ? -1 : 1;
  });

  // Programmatically add 'Products' as the first stage
  jsonData.stages.unshift({
    Name: 'Products',
    Description: 'Products',
    Percentage: 100
  });

  // Read stream stages
  const stageMap = new Map<string, StreamStage>();
  const streamStageOrder: string[] = [];
  jsonData.stages.forEach((stage) => {
    stageMap.set(stage.Name, stage);
    streamStageOrder.push(stage.Name);
  });

  // Read capabilities (keyed by Name::Stage, same capability in different stages = separate instances)
  const capabilityMap = new Map<string, Capability>();
  jsonData.capabilities.forEach((capability) => {
    capabilityMap.set(KeyUtils.forCapabilityGroup(capability.Name, capability.Stage), capability);
  });

  // Create a map of application groups from applicationGroups
  const applicationGroupMap = new Map<string, { Name: string; Description: string }>();
  jsonData.applicationGroups?.forEach((mapping) => {
    if (!applicationGroupMap.has(mapping.ApplicationGroup)) {
      applicationGroupMap.set(mapping.ApplicationGroup, {
        Name: mapping.ApplicationGroup,
        Description: '' // We removed the description field
      });
    }
  });

  // Read team -> capability mapping with optional stage context
  type TeamCapabilityMapping = Array<{ capability: string; stage?: string; percentage?: any }>;
  const teamCapabilities: Map<string, TeamCapabilityMapping> = new Map<
    string,
    TeamCapabilityMapping
  >();
  jsonData.teams.forEach((mapping) => {
    const teamName = mapping.team;
    const capabilityName = mapping.capability;
    const stageName = mapping.stage;
    const percentage = mapping.Percentage;
    if (!teamCapabilities.has(teamName)) {
      teamCapabilities.set(teamName, []);
    }
    teamCapabilities.get(teamName)?.push({ capability: capabilityName, stage: stageName, percentage });
  });

  // Read application -> team -> capability -> stage mapping
  const application2teamMapping = new Map<string, Array<{ team: string; capability?: string; stage?: string; percentage?: any }>>();
  jsonData.applicationGroups?.forEach((mapping) => {
    const appGroupName = mapping.ApplicationGroup;
    const teamName = mapping.Team;
    const capabilityName = mapping.Capability;
    const stageName = mapping.Stage;
    const percentage = mapping.Percentage;
    if (!application2teamMapping.has(appGroupName)) {
      application2teamMapping.set(appGroupName, []);
    }
    application2teamMapping.get(appGroupName)!.push({
      team: teamName,
      capability: capabilityName,
      stage: stageName,
      percentage
    });
  });

  // map nodes to their keys
  const nodeMap = new Map<string, INode>();
  const startNodeMap = new Map<string, INode>();
  let edgeId = 0;

  // Create context object for node creators
  const nodeCreationContext = {
    graph,
    nodeMap,
    nodeId: { value: nodeId },
    capabilityMap,
    applicationGroupMap
  };

  const buildHierarchy = () => {
    // 1. Build capability groups
    capabilityMap.forEach((capability) => {
      if (!capability.Stage) {
        console.warn(`Capability "${capability.Name}" has no stage defined. Skipping.`);
        return;
      }
      createCapaGroupNode(nodeCreationContext, capability.Name, capability.Stage, capability.Percentage);
    });

    // 2. Create team nodes under their respective capabilities
    teamCapabilities.forEach((capabilityMappings, teamName) => {
      capabilityMappings.forEach(({ capability: capabilityName, stage: explicitStageName, percentage }) => {
        const capability = explicitStageName ? capabilityMap.get(KeyUtils.forCapabilityGroup(capabilityName, explicitStageName)) : undefined;
        if (!capability) {
          console.warn(`Capability "${capabilityName}" (stage: ${explicitStageName}) not found for team ${teamName}. Skipping.`);
          return;
        }

        const stageName = explicitStageName || capability.Stage;
        if (!stageName) {
          console.warn(
            `No stage defined for capability "${capabilityName}" and no explicit stage provided. Skipping.`
          );
          return;
        }

        const capaNode = nodeMap.get(KeyUtils.forCapabilityGroup(capabilityName, stageName));
        if (capaNode) {
          createTeamNode(nodeCreationContext, teamName, capaNode, stageName, percentage);
        } else {
          console.warn(
            `Capability node not found for ${capabilityName}-${stageName} when creating team ${teamName}`
          );
        }
      });
    });

    // 3. Create application group nodes under their respective teams
    application2teamMapping.forEach((teamMappings, appGroupName) => {
      if (!applicationGroupMap.has(appGroupName)) {
        return;
      }

      teamMappings.forEach(({ team: teamName, capability: explicitCapabilityName, stage: explicitStageName, percentage }) => {
        if (explicitCapabilityName) {
          const capability = explicitStageName ? capabilityMap.get(KeyUtils.forCapabilityGroup(explicitCapabilityName, explicitStageName)) : undefined;
          if (!capability || !capability.Stage) {
            console.warn(
              ` -> FAILED: Capability '${explicitCapabilityName}' (stage: ${explicitStageName}) not found or has no stage defined.`
            );
            return;
          }

          const teamNodeKey = KeyUtils.forTeamNode(
            teamName,
            explicitCapabilityName,
            capability.Stage
          );
          const teamNode = nodeMap.get(teamNodeKey);
          if (teamNode) {
            const appGroupNode = createAppGroupNode(
              nodeCreationContext,
              appGroupName,
              teamNode,
              explicitCapabilityName,
              capability.Stage,
              percentage
            );
            graph.setParent(appGroupNode, teamNode);
          } else {
            console.warn(
              `[AppGroup Creation Loop] FAILED: Parent team node not found in nodeMap with key '${teamNodeKey}'.`
            );
          }
        } else {
          const capabilityMappingsForTeam = teamCapabilities.get(teamName);
          if (capabilityMappingsForTeam) {
            capabilityMappingsForTeam.forEach(
              ({ capability: capabilityName, stage: explicitStageName }) => {
                const capability = explicitStageName ? capabilityMap.get(KeyUtils.forCapabilityGroup(capabilityName, explicitStageName)) : undefined;
                if (!capability) {
                  console.warn(` -> FAILED: Capability '${capabilityName}' (stage: ${explicitStageName}) not found.`);
                  return;
                }

                const stageName = explicitStageName || capability.Stage;
                if (!stageName) {
                  console.warn(` -> FAILED: No stage defined for capability '${capabilityName}'.`);
                  return;
                }

                const teamNodeKey = KeyUtils.forTeamNode(teamName, capabilityName, stageName);
                const teamNode = nodeMap.get(teamNodeKey);
                if (teamNode) {
                  const appGroupNode = createAppGroupNode(
                    nodeCreationContext,
                    appGroupName,
                    teamNode,
                    capabilityName,
                    stageName,
                    percentage
                  );
                  graph.setParent(appGroupNode, teamNode);
                } else {
                  console.warn(
                    `[AppGroup Creation Loop] FAILED: Parent team node not found in nodeMap with key '${teamNodeKey}'.`
                  );
                }
              }
            );
          } else {
            console.warn(` -> FAILED: No capabilities found for team '${teamName}'.`);
          }
        }
      });
    });
  };

  buildHierarchy();

  // Create leaf nodes from the leafs array
  if (jsonData.leafs && jsonData.leafs.length > 0) {
    jsonData.leafs.forEach((leaf) => {
      const leafName = leaf.Leaf;
      const appName = leaf.Application;
      const stageName = leaf.Stage;
      const teamName = leaf.Team;
      const capabilityName = leaf.Capability;
      const type = leaf.Type;
      const percentage = leaf.Percentage;

      if (!teamName || !appName || !stageName || !leafName || !capabilityName) {
        console.warn(`Missing data in leaf, skipping leaf node creation:`, leaf);
        return;
      }

      const capability = capabilityMap.get(KeyUtils.forCapabilityGroup(capabilityName, stageName));
      if (!capability) {
        console.warn(`Capability "${capabilityName}" (stage: ${stageName}) not found. Skipping leaf.`);
        return;
      }

      application2teamMapping.forEach((teamMappings, appGroupName) => {
        // Only create the leaf node under the application group that matches the leaf's Application property
        if (appName === appGroupName && applicationGroupMap.has(appGroupName)) {
          const hasTeam = teamMappings.some((mapping) => mapping.team === teamName);
          if (hasTeam) {
            const appGroupNodeKey = KeyUtils.forAppGroup(
              appGroupName,
              teamName,
              capabilityName,
              stageName
            );
            const appGroupNode = nodeMap.get(appGroupNodeKey);

            if (appGroupNode) {
              createLeafNode(
                nodeCreationContext,
                appName,
                appGroupNode,
                capabilityName,
                stageName,
                leafName,
                type,
                percentage
              );
            } else {
              console.warn(`Application group node not found with key: ${appGroupNodeKey}`);
            }
          }
        }
      });
    });
  } else {
    console.warn('No leafs array found in data or it is empty. Skipping leaf node creation.');
  }

  /**
   * Connect product node in legend to first leaf nodes in ordered path
   */
  function connectProductToFirstLeafs(
    graph: IGraph,
    data: GraphData,
    productName: string,
    nodeMap: Map<string, INode>,
    productsMap: Map<string, Product>,
    productsArray: Product[],
    maxRevenue: number,
    startNodeMap: Map<string, INode>
  ): void {
    // Debug product names in the data
    // Get the ordered path for this product
    const orderedPath = createOrderedLeafPathForProduct(data, productName);

    if (!orderedPath || orderedPath.length === 0) {
      console.warn(`[DEBUG-CONNECT] No ordered path found for product ${productName}`);
      return;
    }

    // Get all leafs that belong to this product
    const productLeafs = data.leafs.filter(leaf => 
      leaf.Products.includes(productName)
    );
    const productLeafNames = new Set(productLeafs.map(leaf => leaf.Leaf));

    // Create a reverse flow map to identify starting points
    const reverseFlowMap: { [key: string]: string[] } = {};
    data.flows.forEach(flow => {
      if (!reverseFlowMap[flow.TargetLeaf]) {
        reverseFlowMap[flow.TargetLeaf] = [];
      }
      reverseFlowMap[flow.TargetLeaf].push(flow.SourceLeaf);
    });

    // Find all starting points (leafs with no incoming flows from within the product)
    const startingLeafNames = Array.from(productLeafNames).filter(leaf => 
      !reverseFlowMap[leaf] || 
      !reverseFlowMap[leaf].some((source: string) => productLeafNames.has(source))
    );

    // Convert starting leaf names to leaf info objects with stage
    const firstStageLeafs = orderedPath.filter(leafInfo => 
      startingLeafNames.includes(leafInfo.name)
    );

    if (firstStageLeafs.length === 0) {
      console.warn(`[DEBUG-CONNECT] No leafs found in first stage for product ${productName}`);
      return;
    }

    // Try to get the product node directly from the nodeMap using the product key
    const productNodeKey = `product-${productName}`;

    let productNode = nodeMap.get(productNodeKey);
    // If not found by key, try to get it from startNodeMap
    if (!productNode && startNodeMap.has(productName)) {
      productNode = startNodeMap.get(productName);
    }

    // If still not found, try to find it by filtering nodes
    if (!productNode) {
      // Log all product nodes to help debug
      const allNodes = Array.from(nodeMap.values());

      const productNodes = allNodes.filter(
        (node) =>
          node.tag &&
          node.tag.type === 'PRODUCT' &&
          (node.tag.Name === productName || node.tag.product === productName)
      );

      if (productNodes.length === 0) {
        console.warn(`[DEBUG-CONNECT] Product node not found for ${productName}`);
        return;
      }

      productNode = productNodes[0];
    }
    // Connect product node to each first leaf node that has this product
    firstStageLeafs.forEach((leafInfo) => {
      // Find all leaf nodes with this name and stage
      const allLeafNodes = Array.from(nodeMap.values()).filter(
        (node) =>
          node.tag?.type === 'LEAF' &&
          node.tag?.leafName === leafInfo.name &&
          node.tag?.stage === leafInfo.stage
      );

      if (allLeafNodes.length === 0) {
        console.warn(`Leaf node not found for ${leafInfo.name} in stage ${leafInfo.stage}`);
        return;
      }

      // Filter to only include leaf nodes that explicitly have this product
      const leafNodesWithProduct = allLeafNodes.filter((leafNode) => {
        // Find the leaf in the original data that matches all identity fields
        const leaf = data.leafs.find(
          (l) =>
            isSameLeafNodeIdentity(l, {
              leafName: leafNode.tag.leafName,
              Capability: leafNode.tag.capability,
              Stage: leafNode.tag.stage,
              Team: leafNode.tag.team,
              Application: leafNode.tag.parentName
            }) && l.Products.includes(productName)
        );
        return leaf !== undefined;
      });

      if (leafNodesWithProduct.length === 0) {
        console.warn(
          `No leaf nodes found with product ${productName} for ${leafInfo.name} in stage ${leafInfo.stage}`
        );
        return;
      }

      // Create connections to all matching leaf nodes that have this product
      const connections = new Set<string>();
      leafNodesWithProduct.forEach((leafNode) => {
        const connectionKey = `${productNode.tag.nodeId}-${leafNode.tag.nodeId}`;
        if (!connections.has(connectionKey)) {
          const product = productsMap.get(productName);
          if (product) {
            const edgeStyle = animator 
              ? getAnimatedProductEdgeStyle(product, productsArray, maxRevenue, dotPixelsPerSecond)
              : getProductEdgeStyle(product, productsArray, maxRevenue);
            
            graph.createEdge({
              source: productNode,
              target: leafNode,
              tag: { edgeId: edgeId++, type: 'PRODUCT_TO_LEAF', product: productName },
              style: edgeStyle
            });
            connections.add(connectionKey);
          }
        }
      });
    });
  }

  // Function to create edges between leaf nodes for a given product
  function createEdgesForProduct(
    graph: IGraph,
    data: GraphData,
    productName: string,
    nodeMap: Map<string, INode>
  ): void {
    // Get all application nodes
    const appNodes = Array.from(nodeMap.values()).filter(
      (node) => node.tag?.type === 'LEAF'
    );

    // Create a map of flow connections
    const flowConnections = new Map<string, string[]>();
    data.flows.forEach((flow) => {
      if (!flowConnections.has(flow.SourceLeaf)) {
        flowConnections.set(flow.SourceLeaf, []);
      }
      flowConnections.get(flow.SourceLeaf)!.push(flow.TargetLeaf);
    });

    // Process each node as a potential source
    appNodes.forEach((sourceNode) => {
      // Skip if source node doesn't have this product
      const sourceLeafName = sourceNode.tag?.leafName;
      const sourceCapability = sourceNode.tag?.capability;
      const sourceStage = sourceNode.tag?.stage;
      const sourceTeam = sourceNode.tag?.team;
      const sourceApplication = sourceNode.tag?.parentName;

      if (
        !sourceLeafName ||
        !sourceCapability ||
        !sourceStage ||
        !sourceTeam ||
        !sourceApplication
      ) {
        return;
      }

      // Check if any leaf with these properties has this product
      const hasProduct = data.leafs.some(
        (leaf) =>
          isSameLeafNodeIdentity(leaf, {
            leafName: sourceLeafName,
            Capability: sourceCapability,
            Stage: sourceStage,
            Team: sourceTeam,
            Application: sourceApplication
          }) && leaf.Products.includes(productName)
      );

      // Skip if no matching leaf with this product exists
      if (!hasProduct) {
        return;
      }

      // Find potential target leaf names from flow connections
      const targetLeafNames = flowConnections.get(sourceLeafName) || [];
      if (targetLeafNames.length === 0) {
        return;
      }

      // For each potential target, find matching nodes
      targetLeafNames.forEach((targetLeafName) => {
        // Find all nodes that match this target leaf name and have the product
        const potentialTargetNodes = appNodes.filter((node) => {
          // First check if leaf name matches
          if (node.tag?.leafName !== targetLeafName) {
            return false;
          }

          const targetCapability = node.tag?.capability;
          const targetStage = node.tag?.stage;
          const targetTeam = node.tag?.team;
          const targetApplication = node.tag?.parentName;

          // Skip if missing required properties
          if (!targetCapability || !targetStage || !targetTeam || !targetApplication) {
            return false;
          }

          // Find matching leaves with all these properties and the product
          const matchingLeaves = data.leafs.filter(
            (leaf) =>
              isSameLeafNodeIdentity(leaf, {
                leafName: targetLeafName,
                Capability: targetCapability,
                Stage: targetStage,
                Team: targetTeam,
                Application: targetApplication
              }) && leaf.Products.includes(productName)
          );

          if (matchingLeaves.length > 0) {
            return true;
          }
          return false;
        });

        // If no matching nodes with the product, skip to next target
        if (potentialTargetNodes.length === 0) {
          return;
        }

        // If we have multiple potential targets with the same leaf name,
        // filter by territory if possible
        if (potentialTargetNodes.length > 1) {
          const sourceTerritory = sourceNode.tag?.territory;

          if (sourceTerritory) {
            // Filter target nodes to only include those with matching territory
            const matchingTerritoryNodes = potentialTargetNodes.filter((node) => {
              return node.tag?.territory === sourceTerritory;
            });

            // If we found matches with the same territory, use only those
            if (matchingTerritoryNodes.length > 0) {
              potentialTargetNodes.length = 0; // Clear the array
              matchingTerritoryNodes.forEach((node) => potentialTargetNodes.push(node));
            }
          }
        }

        // Process all remaining target nodes (already filtered by product and region)
        potentialTargetNodes.forEach((targetNode) => {
          const targetCapability = targetNode.tag?.capability;
          const targetStage = targetNode.tag?.stage;

          if (!targetCapability || !targetStage) {
            return;
          }

          // For intra-stage connections, check if they share the same hierarchy
          if (sourceStage === targetStage) {
            const sourceTeam = sourceNode.tag?.team;
            const targetTeam = targetNode.tag?.team;
            const sourceParentName = sourceNode.tag?.parentName;
            const targetParentName = targetNode.tag?.parentName;
            const sourceCapability = sourceNode.tag?.capability;
            const targetCapability = targetNode.tag?.capability;

            // Only connect if they share the same team, parent, and capability
            if (
              sourceTeam !== targetTeam ||
              sourceParentName !== targetParentName ||
              sourceCapability !== targetCapability
            ) {
              return;
            }
          }

          // At this point, both source and target nodes have this product and meet all other criteria
          // Create the edge
          const product = productsMap.get(productName);
          if (product) {
            const edgeType = sourceStage === targetStage ? 'INTRA_STAGE_FLOW' : 'INTER_STAGE_FLOW';
            // Only animate inter-stage flows, not intra-stage flows
            const edgeStyle = (animator && edgeType === 'INTER_STAGE_FLOW')
              ? getAnimatedProductEdgeStyle(product, productsArray, maxRevenue, dotPixelsPerSecond)
              : getProductEdgeStyle(product, productsArray, maxRevenue);

            graph.createEdge({
              source: sourceNode,
              target: targetNode,
              tag: { edgeId: edgeId++, type: edgeType, product: productName },
              style: edgeStyle
            });
          }
        });
      });
    });
  }

  // Create edges between leaf nodes based on flow data
  jsonData.products.forEach((product) => {
    createEdgesForProduct(graph, jsonData, product.Name, nodeMap);
  });

  // Create table for layout
  const table = new Table();
  const insets = new Insets(0);
  table.padding = insets;
  table.columnDefaults.padding = insets;
  table.rowDefaults.padding = insets;
  // table.columnDefaults.size = 200; // TODO?

  table.createRow({
    padding: new Insets(50, 50, 50, 50)
  });

  const stage2column = new Map<string, IColumn>();
  const stage2columnGroupNode = new Map<string, INode>();

  streamStageOrder.forEach((stageName, idx) => {
    // Get the stage object from the map
    const stage = stageMap.get(stageName);
    
    // Create column with transparent initial styling
    // Styling will be updated dynamically based on selected performance metric
    const column = table.createColumn({
      style: new NodeStyleStripeStyleAdapter(
        new ShapeNodeStyle({
          fill: 'transparent',
          stroke: 'transparent'
        })
      ),
      padding: new Insets(120, 50, 20, 70)
    });

    if (stageName !== 'Products') {
      // Use default header style initially
      // Style will be updated dynamically based on selected performance metric
      // Get the health percentage for this stage
      const healthPercentage = stage?.PercentageMetrics?.HealthPercentage ?? 0;
      const percentageText = `\n${Math.round(healthPercentage)}%`;
      
      const columnLabel = table.addLabel({
        owner: column,
        text: stageName + percentageText,
        style: columnHeaderStyle,
        tag: { 
          description: stageMap.get(stageName)?.Description ?? 'N.A.',
          stageName: stageName, // Store original stage name for updates
          healthPercentage: healthPercentage
        }
      });
      stage2column.set(stageName, column);

      const columnGroupNode = graph.createGroupNode({
        layout: [0, 0, 100, 100],
        tag: {
          nodeId: nodeId++,
          type: 'COLUMN_GROUP',
          stage: stageName,
          percentage: stage?.PercentageMetrics, // Store PercentageMetrics for dynamic updates
          column: column, // Store column reference for dynamic styling
          columnLabel: columnLabel // Store label reference for dynamic styling
        },
        style: INodeStyle.VOID_NODE_STYLE
      });
      stage2columnGroupNode.set(stageName, columnGroupNode);
    }
  });

  productsMap.forEach((product, name) => {
    const revenue = Number.parseInt(product.Revenue);

    const startNode = graph.createNode({
      tag: {
        ...product,
        revenue,
        product: product.Name,
        nodeId: nodeId++,
        type: 'PRODUCT',
        productStart: true,
        stage: 'Products'
      },
      style: getProductNodeStyle(product, productsArray),
      layout: [0, 0, 200, 100]
    });

    graph.addLabel({
      owner: startNode,
      text: name,
      layoutParameter: new InteriorNodeLabelModel({
        padding: 10
      }).createParameter('top-left'),
      style: productLabelStyle
    });

    // Add product node to nodeMap so it can be found for connections
    const productNodeKey = `product-${name}`;
    nodeMap.set(productNodeKey, startNode);
    startNodeMap.set(name, startNode);
  });

  // Now that all product nodes are created, connect them to first leaf nodes
  jsonData.products.forEach((product) => {
    connectProductToFirstLeafs(
      graph,
      jsonData,
      product.Name,
      nodeMap,
      productsMap,
      productsArray,
      maxRevenue,
      startNodeMap
    );
  });

  const tableNode = graph.createGroupNode({
    layout: table.layout,
    style: new TableNodeStyle({
      table,
      backgroundStyle: INodeStyle.VOID_NODE_STYLE
    }),
    tag: { nodeId: nodeId++, type: 'TABLE' }
  });

  stage2columnGroupNode.forEach((columnGroupNode) => {
    graph.setParent(columnGroupNode, tableNode);
  });

  nodeMap.forEach((node) => {
    if (node.tag && node.tag.type === 'CAPABILITY') {
      const stage = node.tag.stage;
      const columnGroupNode = stage2columnGroupNode.get(stage);
      if (columnGroupNode) {
        graph.setParent(node, columnGroupNode);
      } else {
        graph.setParent(node, tableNode);
      }
    }
  });

  startNodeMap.forEach((node) => {
    graph.setParent(node, tableNode);
  });

  return { stages: streamStageOrder, products: productsArray };
}
